import { Check } from 'lucide-react'
import { useCopyToClipboard } from '../../../hooks/use-copy-to-clipboard'
import { ShareUrlLabel } from '../../../lib/share-url-label'

export interface PostImprovementPromptOverlayProps {
  /** What changed — e.g. "New deck version uploaded", "AI session completed". */
  reasons: string[]
  shareUrl: string
  onDismiss: () => void
}

/**
 * Full-screen takeover shown after an AI session finishes, a new deck
 * uploads, or a profile edit saves — see `usePostImprovementPrompt` for the
 * once-per-day cap. Reuses the same URL-row + copy pattern as
 * `OnboardingLaneOverlay` rather than inventing a new one.
 */
export function PostImprovementPromptOverlay({
  reasons,
  shareUrl,
  onDismiss,
}: PostImprovementPromptOverlayProps) {
  const { copied, copy } = useCopyToClipboard()

  return (
    <div className="fixed inset-0 z-40 grid place-items-center overflow-auto bg-background p-10">
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Close"
        className="fixed top-4 right-5 grid size-8 place-items-center rounded-full border border-border text-muted-foreground"
      >
        ✕
      </button>
      <div className="w-full max-w-[600px] text-center">
        <p className="mb-3.5 text-[11px] font-bold tracking-[0.16em] text-primary uppercase">
          Profile updated
        </p>
        <h2 className="mb-5 text-[32px] font-bold leading-tight tracking-tight">
          Your profile just
          <br />
          got stronger.
        </h2>

        <ul className="mx-auto mb-4 grid max-w-[430px] gap-2 rounded-xl border border-border bg-card p-4 text-left text-[13.5px]">
          {reasons.map((reason) => (
            <li key={reason} className="flex items-start gap-2">
              <Check className="mt-0.5 size-3.5 shrink-0 text-primary" />
              {reason}
            </li>
          ))}
        </ul>

        <p className="mb-4.5 text-[13px] text-muted-foreground">
          Everyone with your link already sees the new version. Send it to the investors who haven't
          opened it yet.
        </p>

        <div className="mx-auto mb-5 flex max-w-[430px] flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2.5 text-left text-[14px]">
          <ShareUrlLabel url={shareUrl} className="truncate" />
          <button
            type="button"
            onClick={() => copy(shareUrl)}
            className="ml-auto rounded-md bg-primary px-3 py-1.5 text-[12.5px] font-bold text-primary-foreground"
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>

        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => copy(shareUrl)}
            className="rounded-xl bg-primary px-5 py-3 text-sm font-bold text-primary-foreground"
          >
            {copied ? 'Copied' : 'Copy your link'}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="text-[13px] text-muted-foreground hover:text-foreground"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  )
}
